import { call, put, takeLatest } from "redux-saga/effects";
import { IBaseResponse } from "../../../interfaces/base";
import {
  ICopyWorkoutInProgramAction,
  ICreateTrainingProgramAction,
  IDeleteTrainingProgramAction,
  IEnableWorkoutInProgramAction,
  IGetTrainingProgramAction,
  IOverrideWorkoutInProgramAction,
  IRemoveWorkoutFromProgramAction,
  ITrainingProgramUpdateBody,
  IUpdateTrainingProgramAction,
} from "../../../interfaces/store/training-program-interface";
import {
  getTrainingProgramFailed,
  getTrainingProgramSuccess,
  getTrainingProgramStart,
  createTrainingProgramStart,
  createTrainingProgramSuccess,
  createTrainingProgramFailed,
  updateTrainingProgramStart,
  updateTrainingProgramSuccess,
  updateTrainingProgramFailed,
  deleteTrainingProgramStart,
  deleteTrainingProgramFailed,
  deleteTrainingProgramSuccess,
  removeWorkoutFromProgramStart,
  removeWorkoutFromProgramSuccess,
  removeWorkoutFromProgramFailed,
  toggleEnableWorkoutInTrainingProgramStart,
  toggleEnableWorkoutInTrainingProgramSuccess,
  toggleEnableWorkoutInTrainingProgramFailed,
  getTrainingProgramActiveUsersStart,
  getTrainingProgramActiveUsersFailed,
  getTrainingProgramActiveUsersSuccess,
  overrideWorkoutInTrainingProgramStart,
  overrideWorkoutInTrainingProgramSuccess,
  overrideWorkoutInTrainingProgramFailed,
  copyWorkoutInTrainingProgramStart,
  copyWorkoutInTrainingProgramFailed,
  copyWorkoutInTrainingProgramSuccess,
  getGroupsStart,
  getGroupsSuccess,
  getGroupsFailed,
} from "./training-program-slice";
import {
  copyWorkoutInProgramApi,
  deleteTrainingProgramApi,
  disableWorkoutInProgramApi,
  enableWorkoutInProgramApi,
  getActiveUsersApi,
  getGroupsApi,
  getTrainingProgramApi,
  removeWorkoutFromProgramApi,
  updateTrainingProgramApi,
} from "../../../api/training-programs";
import { createTrainingProgramApi } from "../../../api/training-programs";
import { IUploadFileParams } from "../../../interfaces/files/files";
import { uploadImage } from "../../utils/fileUpload";
import { base64ToBlob } from "../../../utils/utils";
import { v4 as uuidv4 } from "uuid";

function* _getTrainingProgram({ payload }: IGetTrainingProgramAction) {
  try {
    const { trainingProgramId, trainerId } = payload;

    if (!trainingProgramId) {
      yield put(
        getTrainingProgramFailed({ message: "TrainingProgram ID is required" })
      );
      return;
    }

    const trainingProgram: IBaseResponse = yield call(
      getTrainingProgramApi,
      trainingProgramId
    );

    if (trainingProgram.error) {
      yield put(getTrainingProgramFailed(trainingProgram.error));
    }

    let groups: string[] = [];
    if (trainerId) {
      const dbGroups: IBaseResponse = yield call(getGroupsApi, {
        pagination: {
          page: 0,
          limit: 1000,
          skip: 0,
        },
        filter: {
          trainer: trainerId,
        },
        sort: {
          sortBy: "createdAt",
          sortOrder: 1,
        },
      });

      groups =
        dbGroups.error || !dbGroups.data?.data
          ? []
          : dbGroups.data.data.map((group: any) => {
              return group.group;
            });
    }

    yield put(
      getTrainingProgramSuccess({
        trainingProgram: trainingProgram.data,
        groups,
      })
    );
  } catch (error: any) {
    yield put(getTrainingProgramFailed({ message: error.message }));
  }
}

const base64MediaToBlob = (mediaData: any) => {
  return mediaData
    ? {
        ...mediaData,
        binaryFile: base64ToBlob(mediaData.binaryFile),
      }
    : undefined;
};

function* _createTrainingProgram({ payload }: ICreateTrainingProgramAction) {
  try {
    const response: IBaseResponse = yield call(
      createTrainingProgramApi,
      payload
    );

    const { imageData, largeImageData, videoData } = payload;

    const blobImageData = base64MediaToBlob(imageData);
    const blobLargeImageData = base64MediaToBlob(largeImageData);
    const blobVideoData = base64MediaToBlob(videoData);

    yield call(handleMediaData, blobImageData, response.data._id, "outerCover");
    yield call(
      handleMediaData,
      blobLargeImageData,
      response.data._id,
      "innerCover"
    );
    yield call(handleMediaData, blobVideoData, response.data._id, "video");

    if (response.error) {
      yield put(createTrainingProgramFailed(response.error));
    } else {
      yield put(createTrainingProgramSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(createTrainingProgramFailed({ message: error.message }));
  }
}

function prepareMediaData(mediaData: IUploadFileParams | undefined) {
  return mediaData
    ? {
        ...mediaData,
        binaryFile:
          typeof mediaData.binaryFile === "string"
            ? base64ToBlob(mediaData.binaryFile)
            : mediaData.binaryFile,
      }
    : undefined;
}

function* _updateTrainingProgram({ payload }: IUpdateTrainingProgramAction) {
  try {
    const {
      trainingProgramId,
      trainingProgram,
      imageData,
      videoData,
      largeImageData,
    } = payload;

    const mediaDataMap: Partial<
      Record<keyof ITrainingProgramUpdateBody, IUploadFileParams | undefined>
    > = {
      outerCover: prepareMediaData(imageData),
      innerCover: prepareMediaData(largeImageData),
      video: prepareMediaData(videoData),
    };

    for (const key of Object.keys(mediaDataMap) as Array<
      keyof ITrainingProgramUpdateBody
    >) {
      const uuid = uuidv4();
      const mediaData = mediaDataMap[key];
      if (mediaData && mediaData.binaryFile) {
        yield call(uploadImage, mediaData, trainingProgramId, uuid);

        if (trainingProgram) {
          trainingProgram[key] =
            `${trainingProgramId}/${uuid}${mediaData.fileName}` as any;
        }
      } else if (mediaData && mediaData.binaryFile === null) {
        if (trainingProgram) {
          trainingProgram[key] = null as any;
        }
      }
    }

    const response: IBaseResponse = yield call(updateTrainingProgramApi, {
      trainingProgramId,
      trainingProgram,
    });

    if (response.error) {
      if (response.error === "TRAINING PROGRAM HAS EMPTY WORKOUTS") {
        yield put(
          updateTrainingProgramFailed({
            message:
              "Cannot activate the program because it has missing workouts",
            status: 1000,
          })
        );
      } else {
        yield put(updateTrainingProgramFailed(response.error));
      }
    } else {
      yield put(updateTrainingProgramSuccess(response.data));
    }
  } catch (error: any) {
    if (error.response.data.error === "TRAINING PROGRAM HAS EMPTY WORKOUTS") {
      yield put(
        updateTrainingProgramFailed({
          message:
            "Cannot activate the program because it has missing workouts",
          status: 1000,
        })
      );
    } else {
      yield put(updateTrainingProgramFailed({ message: error.message }));
    }
  }
}

function* _deleteTrainingProgram({ payload }: IDeleteTrainingProgramAction) {
  try {
    const response: IBaseResponse = yield call(
      deleteTrainingProgramApi,
      payload.trainingProgramId
    );

    if (response.error) {
      yield put(deleteTrainingProgramFailed(response.error));
    } else {
      yield put(deleteTrainingProgramSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(deleteTrainingProgramFailed({ message: error.message }));
  }
}

function* handleMediaData(
  mediaData: IUploadFileParams,
  responseId: string,
  fieldName: "image" | "video" | "outerCover" | "innerCover"
) {
  const uuid = uuidv4();
  if (mediaData && responseId) {
    if (mediaData.binaryFile) {
      yield call(uploadImage, mediaData, responseId, uuid);
    }
    yield call(_updateTrainingProgram, {
      payload: {
        trainingProgram: {
          [fieldName]: mediaData.binaryFile
            ? responseId + "/" + uuid + mediaData.fileName
            : null,
        },
        trainingProgramId: responseId,
      },
      type: updateTrainingProgramStart.type,
    });
  }
}

function* _removeWorkoutFromProgram({
  payload,
}: IRemoveWorkoutFromProgramAction) {
  try {
    const { trainingProgramId, workoutId } = payload;
    const response: IBaseResponse = yield call(removeWorkoutFromProgramApi, {
      trainingProgramId,
      workoutId,
    });

    if (response.error) {
      yield put(removeWorkoutFromProgramFailed(response.error));
    } else {
      yield put(removeWorkoutFromProgramSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(removeWorkoutFromProgramFailed({ message: error.message }));
  }
}

function* _toggleEnableWorkoutInTrainingProgram({
  payload,
}: IEnableWorkoutInProgramAction) {
  try {
    const { trainingProgramId, workoutId, enable, index } = payload;

    let response: IBaseResponse;
    if (enable) {
      response = yield call(enableWorkoutInProgramApi, {
        trainingProgramId,
        workoutId,
        index,
      });
    } else {
      response = yield call(disableWorkoutInProgramApi, {
        trainingProgramId,
        workoutId,
      });
    }

    if (response.error) {
      yield put(toggleEnableWorkoutInTrainingProgramFailed(response.error));
    } else {
      yield put(toggleEnableWorkoutInTrainingProgramSuccess(response.data));
    }
  } catch (error: any) {
    let errorMessage = error.message;
    if (
      error.response.data.error === "INDEX ALREADY OCCUPIED IN TRAINING PROGRAM"
    ) {
      errorMessage = "Index already occupied in training program";
    }
    yield put(
      toggleEnableWorkoutInTrainingProgramFailed({ message: errorMessage })
    );
  }
}

function* _overrideWorkoutInTrainingProgram({
  payload,
}: IOverrideWorkoutInProgramAction) {
  try {
    const { trainingProgramId, workoutId, workoutToOverrideId, index } =
      payload;

    const disabledrResponse: IBaseResponse = yield call(
      disableWorkoutInProgramApi,
      {
        trainingProgramId,
        workoutId: workoutToOverrideId,
      }
    );

    const response: IBaseResponse = yield call(enableWorkoutInProgramApi, {
      trainingProgramId,
      workoutId,
      index,
    });

    if (response.error || disabledrResponse.error) {
      yield put(overrideWorkoutInTrainingProgramFailed(response.error));
    } else {
      yield put(overrideWorkoutInTrainingProgramSuccess(response.data));
    }
  } catch (error: any) {
    yield put(
      overrideWorkoutInTrainingProgramFailed({ message: error.message })
    );
  }
}

function* _getActiveUsers({ payload }: any) {
  try {
    const { trainingProgramId, pagination } = payload;
    const response: IBaseResponse = yield call(
      getActiveUsersApi,
      trainingProgramId,
      pagination.skip,
      pagination.limit
    );

    if (response.error) {
      yield put(getTrainingProgramActiveUsersFailed(response.error));
    } else {
      yield put(getTrainingProgramActiveUsersSuccess(response.data));
    }
  } catch (error: any) {
    yield put(getTrainingProgramActiveUsersFailed({ message: error.message }));
  }
}

function* _copyWorkoutInTrainingProgram({
  payload,
}: ICopyWorkoutInProgramAction) {
  try {
    const { trainingProgramId, workoutId, index } = payload;

    const response: IBaseResponse = yield call(copyWorkoutInProgramApi, {
      trainingProgramId,
      workoutId,
      index,
    });

    if (response.error) {
      yield put(copyWorkoutInTrainingProgramFailed(response.error));
    } else {
      yield put(copyWorkoutInTrainingProgramSuccess(response.data));
    }
  } catch (error: any) {
    let errorMessage = error.message;
    if (
      error.response.data.error === "INDEX ALREADY OCCUPIED IN TRAINING PROGRAM"
    ) {
      errorMessage = "Index already occupied in training program";
    }
    yield put(copyWorkoutInTrainingProgramFailed({ message: errorMessage }));
  }
}

function* _getGroups({ payload }: any) {
  try {
    const trainerId = payload;

    if (!trainerId) {
      yield put(getGroupsFailed({ message: "Trainer ID is required" }));
      return;
    }

    const dbGroups: IBaseResponse = yield call(getGroupsApi, {
      pagination: {
        page: 0,
        limit: 1000,
        skip: 0,
      },
      filter: {
        trainer: trainerId,
      },
      sort: {
        sortBy: "createdAt",
        sortOrder: 1,
      },
    });

    const groups =
      dbGroups.error || !dbGroups.data?.data
        ? []
        : dbGroups.data.data.map((group: any) => {
            return group.group;
          });

    yield put(getGroupsSuccess(groups));
  } catch (error: any) {
    yield put(getGroupsFailed({ message: error.message }));
  }
}

function* trainingProgramSaga() {
  yield takeLatest(getTrainingProgramStart.type, _getTrainingProgram);
  yield takeLatest(createTrainingProgramStart.type, _createTrainingProgram);
  yield takeLatest(updateTrainingProgramStart.type, _updateTrainingProgram);
  yield takeLatest(deleteTrainingProgramStart.type, _deleteTrainingProgram);
  yield takeLatest(
    removeWorkoutFromProgramStart.type,
    _removeWorkoutFromProgram
  );
  yield takeLatest(
    toggleEnableWorkoutInTrainingProgramStart.type,
    _toggleEnableWorkoutInTrainingProgram
  );
  yield takeLatest(getTrainingProgramActiveUsersStart.type, _getActiveUsers);
  yield takeLatest(
    overrideWorkoutInTrainingProgramStart.type,
    _overrideWorkoutInTrainingProgram
  );
  yield takeLatest(
    copyWorkoutInTrainingProgramStart.type,
    _copyWorkoutInTrainingProgram
  );
  yield takeLatest(getGroupsStart.type, _getGroups);
}

export default trainingProgramSaga;
